import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, TrendingUp, ArrowRight } from "lucide-react";

const STORAGE_KEY = "finadore_portfolio_popup_dismissed";
const SHOW_DELAY_MS = 4000;

export default function PortfolioReviewPopup() {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    // Don't show on the mutual-funds page itself or admin
    if (pathname === "/mutual-funds" || pathname.startsWith("/admin")) return;
    // Don't show if user already dismissed
    if (localStorage.getItem(STORAGE_KEY)) return;

    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [pathname]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  };

  const handleCTA = () => {
    dismiss();
    navigate("/mutual-funds#portfolio-review");
    setTimeout(() => {
      document.getElementById("portfolio-review")?.scrollIntoView({ behavior: "smooth" });
    }, 400);
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={dismiss}
        style={{
          position: "fixed", inset: 0, zIndex: 9998,
          background: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)",
          animation: "fadein 0.3s ease",
        }}
      />

      {/* Popup card */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Portfolio Review offer"
        data-testid="portfolio-review-popup"
        style={{
          position: "fixed",
          bottom: "clamp(20px, 5vw, 48px)",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          width: "clamp(300px, 90vw, 480px)",
          background: "linear-gradient(135deg, #1e1f52 0%, #2b2d6e 100%)",
          borderRadius: 20,
          boxShadow: "0 32px 80px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06)",
          overflow: "hidden",
          animation: "slideup 0.4s cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        {/* Accent bar */}
        <div style={{ height: 3, background: "linear-gradient(90deg, #4a7c6f, #3b82f6)" }} />

        {/* Close button */}
        <button
          onClick={dismiss}
          data-testid="portfolio-popup-skip"
          aria-label="Skip"
          style={{
            position: "absolute", top: 14, right: 14,
            background: "rgba(255,255,255,0.1)", border: "none",
            borderRadius: "50%", width: 32, height: 32,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", color: "rgba(255,255,255,0.7)",
            transition: "background 0.2s",
          }}
          onMouseEnter={e => (e.currentTarget.style.background = "rgba(255,255,255,0.18)")}
          onMouseLeave={e => (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
        >
          <X size={15} />
        </button>

        <div style={{ padding: "24px 28px 28px" }}>
          {/* Icon + heading */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12, flexShrink: 0,
              background: "rgba(16,185,129,0.15)",
              border: "1px solid rgba(16,185,129,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <TrendingUp size={22} color="#4a7c6f" />
            </div>
            <div>
              <p style={{
                margin: "0 0 4px",
                fontSize: 17, fontWeight: 800,
                color: "white",
                fontFamily: "DM Serif Display, serif",
                lineHeight: 1.3,
              }}>
                Is your portfolio working hard enough?
              </p>
              <p style={{
                margin: 0, fontSize: 13,
                color: "rgba(255,255,255,0.6)",
                fontFamily: "DM Sans, sans-serif",
                lineHeight: 1.5,
              }}>
                Get a complimentary portfolio review from our experts.
              </p>
            </div>
          </div>

          {/* Pills */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 22 }}>
            {["Overlap Analysis", "Risk Profiling", "Fund Recommendations"].map(label => (
              <span key={label} style={{
                fontSize: 11, fontWeight: 600,
                color: "rgba(255,255,255,0.7)",
                background: "rgba(255,255,255,0.08)",
                borderRadius: 20, padding: "4px 11px",
                fontFamily: "DM Sans, sans-serif",
                border: "1px solid rgba(255,255,255,0.1)",
              }}>{label}</span>
            ))}
          </div>

          {/* CTA row */}
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              onClick={handleCTA}
              data-testid="portfolio-popup-cta"
              style={{
                flex: 1,
                background: "#4a7c6f",
                color: "white",
                border: "none",
                borderRadius: 10,
                padding: "12px 18px",
                fontSize: 14, fontWeight: 700,
                fontFamily: "DM Sans, sans-serif",
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                transition: "background 0.2s, transform 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#4a7c6f"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#4a7c6f"; e.currentTarget.style.transform = "none"; }}
            >
              Get Portfolio Reviewed <ArrowRight size={15} />
            </button>
            <button
              onClick={dismiss}
              style={{
                background: "rgba(255,255,255,0.07)",
                color: "rgba(255,255,255,0.55)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 10,
                padding: "12px 16px",
                fontSize: 13, fontWeight: 500,
                fontFamily: "DM Sans, sans-serif",
                cursor: "pointer",
                transition: "background 0.2s",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={e => (e.currentTarget.style.background = "rgba(255,255,255,0.12)")}
              onMouseLeave={e => (e.currentTarget.style.background = "rgba(255,255,255,0.07)")}
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideup {
          from { opacity: 0; transform: translateX(-50%) translateY(40px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes fadein {
          from { opacity: 0; } to { opacity: 1; }
        }
      `}</style>
    </>
  );
}
